/* global globalThis */
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BASE_URL } from "../constants";
import {
  fetchUnreadCount,
  notificationReceived,
  updateUnreadCount,
} from "../store/slices/notificationSlice";

const INITIAL_RECONNECT_DELAY_MS = 1500;
const MAX_RECONNECT_DELAY_MS = 30000;

const clearReconnectTimer = (timerRef) => {
  if (timerRef.current) {
    globalThis.clearTimeout(timerRef.current);
    timerRef.current = null;
  }
};

const useNotificationStream = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const token = useSelector((state) => state.auth.token);

  const eventSourceRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);

  useEffect(() => {
    const clearConnection = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      clearReconnectTimer(reconnectTimerRef);
    };

    if (!isAuthenticated || !token) {
      clearConnection();
      reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
      return clearConnection;
    }

    const scheduleReconnect = () => {
      clearReconnectTimer(reconnectTimerRef);

      reconnectTimerRef.current = globalThis.setTimeout(() => {
        reconnectDelayRef.current = Math.min(
          reconnectDelayRef.current * 2,
          MAX_RECONNECT_DELAY_MS
        );
        connect();
      }, reconnectDelayRef.current);
    };

    const connect = () => {
      const streamUrl = `${BASE_URL}/notifications/stream?token=${encodeURIComponent(token)}`;
      const eventSource = new EventSource(streamUrl);
      eventSourceRef.current = eventSource;

      eventSource.addEventListener("notification", (event) => {
        try {
          const payload = JSON.parse(event.data);
          dispatch(notificationReceived(payload));
        } catch (error) {
          console.error("Failed to parse notification event", error);
        }
      });

      eventSource.addEventListener("notification_count", (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (typeof payload?.unreadCount === "number") {
            dispatch(updateUnreadCount(payload.unreadCount));
          }
        } catch (error) {
          console.error("Failed to parse notification_count event", error);
        }
      });

      eventSource.addEventListener("notification_count_update", () => {
        dispatch(fetchUnreadCount());
      });

      eventSource.onerror = () => {
        clearConnection();
        scheduleReconnect();
      };

      eventSource.onopen = () => {
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
      };
    };

    connect();

    return clearConnection;
  }, [dispatch, isAuthenticated, token]);
};

export default useNotificationStream;
